<script>
import axios from '@/utils/axios';

export default {
  emits: ['updated'],
  data() {
    return {
      lead_id: null,
      isEdit: false,
      editId: null,
      submitting: false,
      errors: {},
      itemOptions: [],
      itemsLoaded: false,

      // Create mode: multiple rows
      rows: [
        { item_id: '', description: '' }
      ],

      // Edit mode: single row
      editForm: {
        item_id: '',
        description: ''
      },
    };
  },

  methods: {

    // ===== Load all items once (dropdown endpoint returns full list) =====
    async loadItems() {
      if (this.itemsLoaded) return;
      try {
        const response = await axios.get('/items/dropdown');
        this.itemOptions = response.data.data || [];
        this.itemsLoaded = true;
      } catch (error) {
        this.$swalToast(error.message, 'error');
      }
    },

    // ===== Open for Add (Create mode) =====
    addRequirement(lead_id) {
      this.resetForm();
      this.lead_id = lead_id;
      this.isEdit = false;
      this.loadItems();
    },

    // ===== Open for Edit (single row update) =====
    async editRequirement(row, lead_id) {
      this.resetForm();
      this.lead_id = lead_id;
      this.isEdit = true;
      this.editId = row.id;
      this.editForm = {
        item_id: row.item_id,
        description: row.description || '',
      };
      await this.loadItems();
    },

    addRow() {
      this.rows.push({ item_id: '', description: '' });
    },

    removeRow(index) {
      if (this.rows.length > 1) {
        this.rows.splice(index, 1);
      }
    },

    async submit() {
      if (this.submitting) return;
      this.submitting = true;
      this.errors = {};
      try {
        if (this.isEdit) {
          const payload = {
            lead_id: this.lead_id,
            item_id: this.editForm.item_id,
            description: this.editForm.description,
          };
          const response = await axios.put(`/lead-requirements/${this.editId}`, payload);
          this.$swalToast(response.data.message);
        } else {
          const payload = {
            lead_id: this.lead_id,
            items: this.rows.map(r => ({
              item_id: r.item_id,
              description: r.description,
            })),
          };
          const response = await axios.post('/lead-requirements', payload);
          this.$swalToast(response.data.message);
        }
        this.$emit('updated');
        this.$closeModal('lead_requirement_modal');
        this.resetForm();
      } catch (error) {
        if (error.type === 'validation') {
          this.errors = error.errors;
          // Special case: duplicate item error comes as errors.items (array of strings)
          if (error.errors?.items) {
            this.$swalToast(error.errors.items[0], 'error');
          }
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.submitting = false;
      }
    },

    resetForm() {
      this.lead_id = null;
      this.isEdit = false;
      this.editId = null;
      this.errors = {};
      this.rows = [{ item_id: '', description: '' }];
      this.editForm = { item_id: '', description: '' };
    },
  },
};
</script>

<template>
  <div class="modal fade" id="lead_requirement_modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEdit ? 'Edit' : 'Add' }} Requirement</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <form @submit.prevent="submit">
          <div class="modal-body">

            <!-- EDIT MODE: single row -->
            <template v-if="isEdit">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Item <span class="text-danger">*</span></label>
                  <vue-select
                    :options="itemOptions"
                    label="name"
                    :reduce="i => i.id"
                    v-model="editForm.item_id"
                    placeholder="Search item by name/code..."
                  />
                  <small class="text-danger" v-if="errors.item_id">{{ errors.item_id[0] }}</small>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Description</label>
                  <input type="text" class="form-control" v-model="editForm.description" placeholder="Enter description" />
                  <small class="text-danger" v-if="errors.description">{{ errors.description[0] }}</small>
                </div>
              </div>
            </template>

            <!-- CREATE MODE: multiple rows with add more -->
            <template v-else>
              <div v-for="(row, index) in rows" :key="index" class="row g-3 align-items-start mb-2 pb-2" style="border-bottom: 1px dashed #e5e7eb;">
                <div class="col-md-5">
                  <label class="form-label" v-if="index === 0">Item <span class="text-danger">*</span></label>
                  <vue-select
                    :options="itemOptions"
                    label="name"
                    :reduce="i => i.id"
                    v-model="row.item_id"
                    placeholder="Search item by name/code..."
                  />
                </div>
                <div class="col-md-6">
                  <label class="form-label" v-if="index === 0">Description</label>
                  <input type="text" class="form-control" v-model="row.description" placeholder="Enter description" />
                </div>
                <div class="col-md-1 d-flex align-items-center" :class="index === 0 ? 'pt-4' : 'pt-1'">
                  <button type="button" class="btn btn-sm btn-outline-danger" @click="removeRow(index)" v-if="rows.length > 1">
                    <i class="ti ti-trash"></i>
                  </button>
                </div>
              </div>

              <button type="button" class="btn btn-sm btn-outline-primary mt-2" @click="addRow">
                <i class="ti ti-plus me-1"></i> Add More
              </button>

              <div v-if="errors.items" class="text-danger small mt-2">{{ errors.items[0] }}</div>
            </template>

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="submitting">
              {{ submitting ? 'Saving...' : (isEdit ? 'Update' : 'Save') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>